import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ShopCartLine = {
  productId: string;
  productHandle: string;
  merchandiseId: string;
  productTitle: string;
  variantTitle: string;
  imageUrl: string | null;
  imageAlt: string | null;
  priceAmount: number;
  currencyCode: string;
  quantity: number;
};

export type ShopRecentlyViewedItem = {
  productId: string;
  productHandle: string;
  productTitle: string;
  imageUrl: string | null;
  imageAlt: string | null;
  priceAmount: number;
  currencyCode: string;
  category: string;
};

type ShopState = {
  lines: ShopCartLine[];
  recentlyViewed: ShopRecentlyViewedItem[];
  addItem: (line: ShopCartLine) => void;
  markRecentlyViewed: (item: ShopRecentlyViewedItem) => void;
  setQuantity: (merchandiseId: string, quantity: number) => void;
  removeItem: (merchandiseId: string) => void;
  clearCart: () => void;
};

export const useShopStore = create<ShopState>()(
  persist(
    (set) => ({
      lines: [],
      recentlyViewed: [],
      addItem: (line) => set((state) => {
        const existing = state.lines.find((item) => item.merchandiseId === line.merchandiseId);
        if (!existing) {
          return { lines: [...state.lines, line] };
        }

        return {
          lines: state.lines.map((item) => item.merchandiseId === line.merchandiseId
            ? { ...item, quantity: item.quantity + line.quantity }
            : item),
        };
      }),
      markRecentlyViewed: (item) => set((state) => ({
        recentlyViewed: [
          item,
          ...state.recentlyViewed.filter((existing) => existing.productHandle !== item.productHandle),
        ].slice(0, 8),
      })),
      setQuantity: (merchandiseId, quantity) => set((state) => ({
        lines: quantity <= 0
          ? state.lines.filter((item) => item.merchandiseId !== merchandiseId)
          : state.lines.map((item) => item.merchandiseId === merchandiseId
            ? { ...item, quantity }
            : item),
      })),
      removeItem: (merchandiseId) => set((state) => ({
        lines: state.lines.filter((item) => item.merchandiseId !== merchandiseId),
      })),
      clearCart: () => set({ lines: [] }),
    }),
    {
      name: 'shop-cart-storage',
      partialize: (state) => ({ lines: state.lines, recentlyViewed: state.recentlyViewed }),
    },
  ),
);